import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings, Check } from "lucide-react";
import { Button } from "./ui/button";

const COOKIE_CONSENT_KEY = "rockmaps_cookie_consent";
const COOKIE_PREFERENCES_KEY = "rockmaps_cookie_preferences";

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    preferences: true,
    analytics: false,
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to avoid flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      preferences: true,
      analytics: true,
    };
    saveConsent(allAccepted);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      preferences: false,
      analytics: false,
    };
    saveConsent(necessaryOnly);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  // Function to reset consent (for footer link)
  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    setShowBanner(true);
    setShowSettings(false);
  };

  // Expose reset function globally for footer link
  useEffect(() => {
    window.resetCookieConsent = resetConsent;
    return () => {
      delete window.resetCookieConsent;
    };
  }, []);

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-500">
      <div className="max-w-4xl mx-auto">
        <div className="bg-[#111] border-2 border-zinc-800 shadow-2xl">
          {/* Main Banner */}
          {!showSettings ? (
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-bold text-lg mb-2">
                    UTILIZAMOS COOKIES
                  </h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    Usamos cookies propias para que el sitio funcione correctamente y mejorar tu experiencia. 
                    Puedes aceptar todas, solo las necesarias, o personalizar tus preferencias.
                  </p>
                  <p className="text-zinc-500 text-xs mb-4">
                    Más información en nuestra{" "}
                    <Link to="/politica-cookies" className="text-primary hover:underline">
                      Política de Cookies
                    </Link>{" "}
                    y{" "}
                    <Link to="/privacidad" className="text-primary hover:underline">
                      Política de Privacidad
                    </Link>
                  </p>
                  
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-primary hover:bg-primary/90 text-white font-mono"
                      data-testid="cookie-accept-all"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      ACEPTAR TODAS
                    </Button>
                    <Button
                      onClick={handleAcceptNecessary}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-mono"
                      data-testid="cookie-accept-necessary"
                    >
                      SOLO NECESARIAS
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      className="text-zinc-400 hover:text-white font-mono"
                      data-testid="cookie-settings"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      CONFIGURAR
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  CONFIGURAR COOKIES
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-zinc-500 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="bg-[#0a0a0a] border border-zinc-800 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-bold text-sm">COOKIES NECESARIAS</h4>
                      <p className="text-zinc-500 text-xs mt-1">
                        Esenciales para el funcionamiento del sitio. No se pueden desactivar.
                      </p>
                    </div>
                    <div className="bg-green-500/20 text-green-400 font-mono text-xs px-3 py-1">
                      SIEMPRE ACTIVAS
                    </div>
                  </div>
                </div>

                {/* Preference Cookies */}
                <div className="bg-[#0a0a0a] border border-zinc-800 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-bold text-sm">COOKIES DE PREFERENCIAS</h4>
                      <p className="text-zinc-500 text-xs mt-1">
                        Recuerdan tus preferencias (ciudad, tema) entre visitas.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.preferences}
                        onChange={(e) => setPreferences({ ...preferences, preferences: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-[#0a0a0a] border border-zinc-800 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-bold text-sm">COOKIES ANALÍTICAS</h4>
                      <p className="text-zinc-500 text-xs mt-1">
                        Nos ayudan a entender cómo usas el sitio para mejorarlo.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="bg-primary hover:bg-primary/90 text-white font-mono flex-1"
                  data-testid="cookie-save-preferences"
                >
                  GUARDAR PREFERENCIAS
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-mono"
                >
                  ACEPTAR TODAS
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Export helper to check cookie preferences
export const getCookiePreferences = () => {
  const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (prefs) {
    return JSON.parse(prefs);
  }
  return { necessary: true, preferences: false, analytics: false };
};

export const hasAcceptedCookies = () => {
  return localStorage.getItem(COOKIE_CONSENT_KEY) === "true";
};
